import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'radix-ui';
import { LayoutGridIcon } from 'lucide-react';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import copyCatalog from '@/i18n/ns/app-sharing';
import appPageCopy from '@/i18n/ns/app-page';
import { companyRequest } from '@/company/api';
import { useCompany } from '@/company/context';
import { useOptionalWorkspace } from '../context';
import { useIntlLocale } from '../intl-locale';
import {
  AudienceControl,
  PeopleList,
  PersonRow,
  SheetButton,
  SheetNote,
  SheetSection,
  ShareSheet,
  audienceStatus,
  roleOptions,
  sameAudience,
  toSheetAudience,
  toVisibility,
  useColleagues,
  useSheetCopy,
  type SheetAudience,
  type SheetMember,
  type SheetRole,
} from '../share-sheet/share-sheet';
import { RunsOn } from '../share-sheet/runs-on';
import { PublicLinkSection } from './publish/public-link';
import { openAppChat } from './open-app-chat';
import type {
  AppShareChoice,
  AppShareCommand,
  AppSharingState,
} from '../../../src/app-data/app-policy/sharing.mjs';

type Selection = Pick<AppShareChoice, 'artifactId' | 'versionId' | 'revisionId'>;
type AppSharingSnapshot = AppSharingState & {
  privateConnections?: {
    name: string | null;
    personName: string | null;
    own: boolean;
    appRead: boolean;
  }[];
};

/** The App's Share sheet: who can open it, with what role, and what it runs on. */
export function useAppSharing({
  selection,
  session,
  canShare,
  canUnshare,
  changed,
  title,
}: {
  selection: Selection | null;
  session: typeof fetch;
  canShare: boolean;
  canUnshare: boolean;
  changed: () => void;
  /** The App's name, for the sheet's heading. */
  title?: string;
}) {
  const [action, setAction] = useState<'share' | 'unshare' | null>(null);
  // Callers pass a new selection object and a new changed callback on every
  // render. Keyed on the selection's ids, with the callback read through a
  // ref, the result stays the same object until something real changes.
  const latest = useRef(changed);
  latest.current = changed;
  const key = selection
    ? [selection.artifactId, selection.versionId, selection.revisionId].join('\u0000')
    : null;
  const selected = useMemo(() => selection, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return useMemo(
    () => ({
      onShare: canShare ? () => setAction('share') : undefined,
      onUnshare: canUnshare ? () => setAction('unshare') : undefined,
      overlay:
        action && selected ? (
          <AppSharingPanel
            key={key}
            selection={selected}
            session={session}
            title={title}
            withdrawing={action === 'unshare'}
            close={() => setAction(null)}
            changed={() => latest.current()}
          />
        ) : null,
    }),
    [action, selected, key, session, title, canShare, canUnshare],
  );
}

function privateSources(state: AppSharingSnapshot | null, unnamed: string) {
  return [
    ...new Set(
      (state?.privateConnections ?? []).map((connection) =>
        connection.personName
          ? `${connection.personName}${connection.name ? ` (${connection.name})` : ''}`
          : connection.name || unnamed,
      ),
    ),
  ].join(', ');
}

function AppSharingPanel({
  selection,
  session,
  title,
  withdrawing,
  close,
  changed,
}: {
  selection: Selection;
  session: typeof fetch;
  title?: string;
  withdrawing: boolean;
  close: () => void;
  changed: () => void;
}) {
  const copy = useNamespaceCopy(copyCatalog);
  const page = useNamespaceCopy(appPageCopy);
  const sheet = useSheetCopy();
  const locale = useIntlLocale();
  const colleagues = useColleagues();
  const company = useCompany();
  const ws = useOptionalWorkspace();
  const companyName = company.data?.company?.name ?? copy.company;
  const [state, setState] = useState<AppSharingSnapshot | null>(null);
  const [audience, setAudience] = useState<SheetAudience>('private');
  const [members, setMembers] = useState<SheetMember[]>([]);
  const [companyRole, setCompanyRole] = useState<SheetRole>('viewer');
  const [busy, setBusy] = useState(false);
  const [command, setCommand] = useState<AppShareCommand | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const alive = useRef(false);
  useLayoutEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const path = `/api/v1/company/apps/${encodeURIComponent(selection.artifactId)}/sharing`;
  useEffect(() => {
    let alive = true;
    const query = new URLSearchParams({
      versionId: selection.versionId,
      revisionId: selection.revisionId,
    });
    void companyRequest<AppSharingSnapshot>(`${path}?${query}`, 'GET', undefined, session)
      .then((value) => {
        if (!alive) return;
        setState(value);
        setAudience(toSheetAudience(value.visibility));
        setMembers(value.audience);
        setCompanyRole(value.companyShareRole ?? 'viewer');
      })
      .catch(() => {
        if (alive) setFailure(copy.stateUnavailable);
      });
    return () => {
      alive = false;
    };
  }, [path, selection.versionId, selection.revisionId, session, copy.stateUnavailable]);
  const visibility = withdrawing ? 'private' : toVisibility(audience);
  async function confirm() {
    if (!state || !alive.current) return;
    setBusy(true);
    setFailure(null);
    const proposal = {
      ...selection,
      visibility,
      audience: visibility === 'members' ? members : [],
      ...(visibility === 'company' ? { companyShareRole: companyRole } : {}),
      expectedSharingRevision: state.sharingRevision,
      expectedPolicyVersion: state.policyVersion,
    };
    try {
      const original =
        command ??
        (await companyRequest<AppShareCommand>(`${path}/prepare`, 'POST', proposal, session));
      if (!alive.current) return;
      setCommand(original);
      const shared = await companyRequest<{ check?: string }>(
        path,
        'POST',
        { command: original, confirmed: true },
        session,
      );
      if (!alive.current) return;
      close();
      // The change is saved; others can open the App once its check finishes in the background.
      if (shared?.check === 'checking') ws?.notify(copy.sharedChecking, null, 'info');
      // The change is committed: refreshing what shows it never reports a failed share.
      try {
        changed();
      } catch {
        /* the next read shows it */
      }
    } catch (error) {
      if (!alive.current) return;
      const code = error instanceof Error && 'code' in error ? String(error.code) : '';
      setFailure(
        code.startsWith('scan') || code.includes('check') ? copy.checkFailed : copy.changed,
      );
    } finally {
      if (alive.current) setBusy(false);
    }
  }
  const changeInChat = () => {
    close();
    void openAppChat(selection).catch(() =>
      ws?.notify(page.changeInChatUnavailable, null, 'error'),
    );
  };
  const locked = busy || command !== null;
  // Nothing to confirm while the audience is what the App already has. Until
  // its state is read, the confirm button shows, disabled.
  const dirty =
    withdrawing ||
    command !== null ||
    state === null ||
    visibility !== state.visibility ||
    (visibility === 'members' && !sameAudience(members, state.audience)) ||
    (visibility === 'company' && companyRole !== (state.companyShareRole ?? 'viewer'));
  const owner = state
    ? colleagues.people.find((person) => person.userId === state.ownerId)
    : undefined;
  const ownerName = owner
    ? owner.userId === ws?.user?.id
      ? formatCopy(sheet.you, { name: owner.displayName || owner.email })
      : owner.displayName || owner.email
    : null;
  const sources = privateSources(state, copy.unnamedConnection);
  const status = failure ? (
    <span role="alert">{failure}</span>
  ) : state && !withdrawing ? (
    audienceStatus(sheet, locale, audience, members.length, companyName)
  ) : undefined;
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open && !busy) close();
      }}
    >
      <ShareSheet
        eyebrow={withdrawing ? copy.unshareTitle : sheet.eyebrowShare}
        title={title || copy.shareTitle}
        icon={<LayoutGridIcon />}
        busy={busy}
        testId="app-share-sheet"
        status={status}
        actions={
          !dirty ? (
            <Dialog.Close asChild>
              <SheetButton primary>{sheet.done}</SheetButton>
            </Dialog.Close>
          ) : (
            <>
              <Dialog.Close asChild>
                <SheetButton disabled={busy}>{copy.cancel}</SheetButton>
              </Dialog.Close>
              <SheetButton
                primary
                data-testid="app-share-confirm"
                disabled={
                  !state || busy || (!withdrawing && audience === 'people' && members.length === 0)
                }
                onClick={() => void confirm()}
              >
                {busy
                  ? copy.checking
                  : command
                    ? copy.checkAgain
                    : withdrawing
                      ? copy.unshare
                      : copy.share}
              </SheetButton>
            </>
          )
        }
      >
        {withdrawing ? (
          <p className="ws-sharesheet-help">{copy.unshareBody}</p>
        ) : (
          state && (
            <>
              <SheetSection title={sheet.whoTitle} help={copy.shareBody}>
                <AudienceControl
                  value={audience}
                  companyName={companyName}
                  disabled={locked}
                  reasons={{
                    ...(state.canCompany ? {} : { company: sheet.companyNeedsEdit }),
                    ...(state.canUnshare || state.visibility === 'private'
                      ? {}
                      : { private: sheet.privateNeedsOwner }),
                  }}
                  onChange={setAudience}
                />
                {audience !== 'private' && sources && (
                  <SheetNote testId="app-share-private-reads">
                    {formatCopy(copy.privateReadWarning, { sources })}
                  </SheetNote>
                )}
                {audience === 'company' && (
                  <PersonRow
                    id="company"
                    company
                    name={formatCopy(sheet.everyone, { company: companyName })}
                    detail={sheet.everyoneRowBody}
                    role={companyRole}
                    roles={roleOptions(sheet, 'work')}
                    disabled={locked}
                    onRole={setCompanyRole}
                  />
                )}
                {audience === 'people' && (
                  <PeopleList
                    members={members}
                    colleagues={colleagues.people}
                    loading={colleagues.loading}
                    failed={colleagues.failed}
                    roles={roleOptions(sheet, 'work')}
                    exclude={[state.ownerId, ws?.user?.id].filter((id): id is string =>
                      Boolean(id),
                    )}
                    disabled={locked}
                    fixed={
                      ownerName ? (
                        <PersonRow
                          id={state.ownerId}
                          name={ownerName}
                          detail={owner?.displayName ? owner.email : undefined}
                          fixedLabel={sheet.owner}
                        />
                      ) : null
                    }
                    onChange={setMembers}
                  />
                )}
              </SheetSection>
              <RunsOn
                kind="result"
                id={selection.artifactId}
                thing="theApp"
                selfId={ws?.user?.id}
                session={session}
                chatLabel={sheet.changeWithChat}
                onChangeInChat={changeInChat}
              />
              <PublicLinkSection selection={selection} session={session} />
            </>
          )
        )}
      </ShareSheet>
    </Dialog.Root>
  );
}
